import { supabase } from './supabase';
import { apiKeyService } from './apiKeyService';
import { 
  PageSpeedInsightsParams,
  PageSpeedResult
} from './types';

export const pagespeedService = {
  /**
   * Récupère la clé API pour Google PageSpeed Insights
   */
  async getApiKey(): Promise<string | null> {
    try {
      // Utiliser le service unifié apiKeyService pour récupérer la clé API
      const apiKey = await apiKeyService.getApiKey('pagespeed');
      
      if (!apiKey) {
        return null;
      }
      
      return apiKey;
    } catch (error) {
      console.error('Error fetching PageSpeed API key:', error);
      return null;
    }
  },

  /**
   * Sauvegarde la clé API pour Google PageSpeed Insights
   */
  async saveApiKey(apiKey: string): Promise<boolean> {
    try {
      // Utiliser le service unifié apiKeyService pour sauvegarder la clé API
      const success = await apiKeyService.saveApiKey(
        'pagespeed', 
        apiKey, 
        { enabled: true }
      );
      
      if (!success) {
        console.error('Erreur lors de la sauvegarde de la clé API PageSpeed');
        return false;
      }
      
      return true;
    } catch (error) {
      console.error('Error saving PageSpeed API key:', error);
      return false;
    }
  },

  /**
   * Teste la validité de la clé API PageSpeed Insights
   */
  async testApiKey(apiKey: string): Promise<boolean> {
    return this.testConnection(apiKey);
  },

  /**
   * Teste la connexion à l'API PageSpeed Insights avec l'URL fournie
   */
  async testConnection(apiKey: string, testUrl: string = 'https://smart-impact.ch'): Promise<boolean> {
    try {
      const url = new URL('https://pagespeedonline.googleapis.com/pagespeedonline/v5/runPagespeed');
      url.searchParams.append('url', testUrl);
      url.searchParams.append('key', apiKey);
      
      const response = await fetch(url.toString());
      
      if (!response.ok) {
        throw new Error(`API returned status ${response.status}`);
      }
      
      const data = await response.json();
      return !!data.lighthouseResult;
    } catch (error) {
      console.error('PageSpeed API test failed:', error);
      return false;
    }
  },

  /**
   * Analyse une URL avec Google PageSpeed Insights
   * @param url URL à analyser
   * @param prospectId ID du prospect (optionnel)
   * @param options Options d'analyse
   * @param options.quickMode Mode rapide (performance uniquement) - défaut: true
   * @param options.mobileOnly Analyser uniquement mobile - défaut: false
   */
  async analyzeSite(
    url: string,
    prospectId?: string,
    options?: {
      quickMode?: boolean;
      mobileOnly?: boolean;
    }
  ): Promise<PageSpeedResult | null> {
    const quickMode = options?.quickMode !== false; // true par défaut
    const mobileOnly = options?.mobileOnly || false;
    // Validation de l'URL
    if (!url || url.trim() === '') {
      console.warn('PageSpeed analysis skipped: empty URL');
      return null;
    }

    try {
      new URL(url);
    } catch (urlError) {
      console.warn('PageSpeed analysis skipped: invalid URL format:', url);
      return null;
    }

    const apiKey = await this.getApiKey();
    
    if (!apiKey) {
      console.error('PageSpeed API key not found');
      return null;
    }
    
    try {
      // OPTIMISATION: Sélection dynamique des catégories
      const categories = quickMode
        ? ['performance'] // Rapide : ~10-15s par stratégie
        : ['performance', 'accessibility', 'best-practices', 'seo']; // Complet : ~30-40s par stratégie

      const modeLabel = quickMode ? '⚡ Mode rapide' : '🔍 Mode complet';
      const strategyLabel = mobileOnly ? '📱 Mobile uniquement' : '📱🖥️ Mobile + Desktop';


      const startTime = Date.now();

      let mobileResult, desktopResult;

      if (mobileOnly) {
        // Mode mobile uniquement (le plus rapide)
        mobileResult = await this.runPageSpeedTest({
          url,
          strategy: 'mobile',
          category: categories
        }, apiKey);

        // Utiliser les scores mobile pour desktop aussi
        desktopResult = mobileResult;
      } else {
        // OPTIMISATION: Exécuter mobile et desktop en PARALLÈLE
        [mobileResult, desktopResult] = await Promise.all([
          this.runPageSpeedTest({
            url,
            strategy: 'mobile',
            category: categories
          }, apiKey),
          this.runPageSpeedTest({
            url,
            strategy: 'desktop',
            category: categories
          }, apiKey)
        ]);
      }

      const duration = ((Date.now() - startTime) / 1000).toFixed(1);

      if (!mobileResult || !desktopResult) {
        throw new Error('Failed to get PageSpeed results');
      }
      
      // Extraire les scores de performance directement à partir des données brutes
      // pour éviter toute incompétence dans l'extraction
      const extractPerformanceScore = (rawData: any): number => {
        try {
          if (rawData && 
              rawData.lighthouseResult && 
              rawData.lighthouseResult.categories && 
              rawData.lighthouseResult.categories.performance && 
              typeof rawData.lighthouseResult.categories.performance.score === 'number') {
            return rawData.lighthouseResult.categories.performance.score;
          }
        } catch (error) {
          console.error('Erreur lors de l\'extraction du score de performance:', error);
        }
        return 0;
      };
      
      const mobileScore = extractPerformanceScore(mobileResult.raw_data);
      const desktopScore = extractPerformanceScore(desktopResult.raw_data);
      
      const result: PageSpeedResult = {
        url,
        // Pour la compatibilité, on garde performance_score comme le score mobile
        performance_score: mobileResult.performance_score,
        accessibility_score: mobileResult.accessibility_score,
        best_practices_score: mobileResult.best_practices_score,
        seo_score: mobileResult.seo_score,
        // Scores spécifiques par plateforme extraits directement des données JSON
        mobile_performance_score: mobileScore,
        desktop_performance_score: desktopScore,
        // Données brutes
        mobile_data: mobileResult.raw_data,
        desktop_data: desktopResult.raw_data,
        prospect_id: prospectId
      };
      
      // Save results to database if prospectId is provided
      if (prospectId) {
        await this.saveResults(result);
      }
      
      return result;
    } catch (error) {
      console.error('Error analyzing site with PageSpeed:', {
        message: error instanceof Error ? error.message : 'Unknown error',
        url: url,
        prospectId: prospectId,
        error: error
      });
      return null;
    }
  },

  /**
   * Sauvegarde les résultats d'analyse dans la base de données
   */
  async saveResults(result: PageSpeedResult): Promise<boolean> {
    try {
      // Vérifier si des résultats existent déjà pour ce prospect
      if (result.prospect_id) {
        const { data } = await supabase
          .from('pagespeed_results')
          .select('id')
          .eq('prospect_id', result.prospect_id)
          .order('created_at', { ascending: false })
          .limit(1);
        
        // Si des résultats existent, mettre à jour plutôt qu'insérer
        if (data && data.length > 0) {
          return this.updateResults(data[0].id, result);
        }
      }
      
      // Insertion d'un nouveau résultat
      const { error } = await supabase
        .from('pagespeed_results')
        .insert({
          prospect_id: result.prospect_id,
          url: result.url,
          performance_score: result.performance_score,
          accessibility_score: result.accessibility_score,
          best_practices_score: result.best_practices_score,
          seo_score: result.seo_score,
          mobile_performance_score: result.mobile_performance_score,
          desktop_performance_score: result.desktop_performance_score,
          desktop_data: result.desktop_data,
          mobile_data: result.mobile_data
        });
      
      if (error) {
        throw error;
      }
      
      return true;
    } catch (error) {
      console.error('Error saving PageSpeed results:', error);
      return false;
    }
  },
  
  /**
   * Met à jour les résultats d'analyse existants
   */
  async updateResults(id: string, result: PageSpeedResult): Promise<boolean> {
    try {
      const { error } = await supabase
        .from('pagespeed_results')
        .update({
          url: result.url,
          performance_score: result.performance_score,
          accessibility_score: result.accessibility_score,
          best_practices_score: result.best_practices_score,
          seo_score: result.seo_score,
          mobile_performance_score: result.mobile_performance_score,
          desktop_performance_score: result.desktop_performance_score,
          desktop_data: result.desktop_data,
          mobile_data: result.mobile_data,
          updated_at: new Date().toISOString()
        })
        .eq('id', id);
      
      if (error) {
        throw error;
      }
      
      return true;
    } catch (error) {
      console.error('Error updating PageSpeed results:', error);
      return false;
    }
  },
  
  /**
   * Supprime les résultats d'analyse
   */
  async deleteResults(id: string): Promise<boolean> {
    try {
      const { error } = await supabase
        .from('pagespeed_results')
        .delete()
        .eq('id', id);
      
      if (error) {
        throw error;
      }
      
      return true;
    } catch (error) {
      console.error('Error deleting PageSpeed results:', error);
      return false;
    }
  },
  
  /**
   * Supprime tous les résultats d'analyse pour un prospect
   */
  async deleteResultsByProspectId(prospectId: string): Promise<boolean> {
    try {
      const { error } = await supabase
        .from('pagespeed_results')
        .delete()
        .eq('prospect_id', prospectId);
      
      if (error) {
        throw error;
      }
      
      return true;
    } catch (error) {
      console.error('Error deleting prospect\'s PageSpeed results:', error);
      return false;
    }
  },

  /**
   * Récupère les résultats d'analyse pour un prospect
   */
  async getResultsByProspectId(prospectId: string): Promise<PageSpeedResult | null> {
    try {
      const { data, error } = await supabase
        .from('pagespeed_results')
        .select('*')
        .eq('prospect_id', prospectId)
        .order('created_at', { ascending: false })
        .limit(1)
        .maybeSingle();
      
      if (error) {
        console.error('Error fetching PageSpeed results:', error);
        return null;
      }
      
      if (!data) {
        return null;
      }
      
      return data as PageSpeedResult;
    } catch (error) {
      console.error('Error fetching PageSpeed results:', error);
      return null;
    }
  },

  /**
   * Exécute un test PageSpeed et traite les résultats
   */
  async runPageSpeedTest(
    params: PageSpeedInsightsParams,
    apiKey: string
  ): Promise<{
    performance_score: number;
    accessibility_score: number;
    best_practices_score: number;
    seo_score: number;
    raw_data: any;
  } | null> {
    try {
      const url = new URL('https://pagespeedonline.googleapis.com/pagespeedonline/v5/runPagespeed');

      // Add parameters to URL
      url.searchParams.append('url', params.url);
      url.searchParams.append('key', apiKey);

      if (params.strategy) {
        url.searchParams.append('strategy', params.strategy);
      }

      if (params.locale) {
        url.searchParams.append('locale', params.locale);
      }

      if (params.category && params.category.length > 0) {
        params.category.forEach(cat => {
          url.searchParams.append('category', cat);
        });
      }


      const response = await fetch(url.toString());

      if (!response.ok) {
        const errorText = await response.text();
        console.error(`❌ PageSpeed API error (${response.status}):`, errorText);
        throw new Error(`API returned status ${response.status}: ${errorText}`);
      }

      const data = await response.json();

      if (!data.lighthouseResult) {
        console.error('❌ Invalid PageSpeed response - missing lighthouseResult');
        throw new Error('Invalid PageSpeed response');
      }

      // Extract scores
      const { categories } = data.lighthouseResult;

      const result = {
        performance_score: categories.performance?.score || 0,
        accessibility_score: categories.accessibility?.score || 0,
        best_practices_score: categories['best-practices']?.score || 0,
        seo_score: categories.seo?.score || 0,
        raw_data: data
      };


      return result;
    } catch (error) {
      console.error('❌ PageSpeed test error:', {
        message: error instanceof Error ? error.message : 'Unknown error',
        url: params.url,
        strategy: params.strategy,
        error: error
      });
      return null;
    }
  }
};
